import { useState } from 'react'

import { Field } from '../../../models/Field'
import { PSRecord } from '../../../models/PSRecord'
import { Type } from '../../../models/Type'
import { NPSRecordState, NPSViewModel } from './NPSViewModel'

export type NPSWorkflowStep<Id extends string> = {
    id: Id
    validate?: (state: NPSRecordState) => boolean
    /**
     * Determines values to be set on next, rather than on field input.
     */
    valuesOnNext?: Record<string, unknown>
    shouldShowFooter?: boolean
    /**
     * Whether the step should be centered on the page.
     */
    isCentered?: boolean
}

export type UseNPSWorkflowProps<StepId extends string> = {
    data: { form: Type; fields: Field[]; record?: PSRecord }
    fields: readonly string[]
    steps: readonly NPSWorkflowStep<StepId>[]
    initialStep: StepId
    /**
     * Whether to run save logic between steps.
     */
    shouldSaveOnNext?: boolean
    shouldConfirmClose?:
        | boolean
        | ((step: NPSWorkflowStep<StepId>, form: NPSRecordState) => boolean)
    onComplete?: () => Promise<void>
    onNext?: (curr: StepId, next: StepId) => Promise<void>
    onClose?: () => Promise<void>
}

export type NPSWorkflowState<StepId extends string> = {
    currentStep: NPSWorkflowStep<StepId>
    currentStepIndex: number
    setStep: (id: StepId) => void
    hasNext: boolean
    isNextDisabled: boolean
    isSaving: boolean
    /**
     * Generic indicator for any transition between steps, close, or completion.
     */
    isTransitioning: boolean
    isCloseConfirmationActive: boolean
    recordForm: NPSRecordState

    next: () => Promise<void>
    complete: () => Promise<void>
    close: () => Promise<void>
    cancelClose: () => void
}

/**
 * Handles record form state across a workflow.
 *
 * TODO The record state could likely be factored out, moving this towards a generic workflow
 *      form state manager.
 */
export function useNPSFormWorkflowCoordinator<StepId extends string>({
    initialStep,
    data: { form, record: initialRecord, fields },
    steps,
    onComplete,
    onClose,
    onNext,
    shouldConfirmClose: shouldConfirmCloseSpec = false,
    shouldSaveOnNext = false,
}: UseNPSWorkflowProps<StepId>): NPSWorkflowState<StepId> {
    const [stepId, setStepId] = useState(initialStep)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const currentStepIndex = steps.findIndex((step) => step.id === stepId)
    const currentStep = steps[currentStepIndex]

    if (currentStep === undefined) {
        throw new Error('Invalid form workflow state.')
    }

    const { validate, valuesOnNext } = currentStep

    const recordForm = NPSViewModel({
        form,
        fields,
        initialRecord,
    })

    const { setValues, save, isSaving } = recordForm

    const isValid = validate?.(recordForm) ?? true

    const hasNext = currentStepIndex < steps.length - 1

    const nextStep = steps[currentStepIndex + 1]

    const shouldConfirmClose =
        typeof shouldConfirmCloseSpec === 'boolean'
            ? () => shouldConfirmCloseSpec
            : shouldConfirmCloseSpec

    const [isCloseConfirmationActive, setIsCloseConfirmationActive] = useState(false)

    const next = async () => {
        if (hasNext) {
            setIsTransitioning(true)

            let override
            if (valuesOnNext) {
                override = setValues(valuesOnNext)
            }

            if (shouldSaveOnNext) {
                await save(override)
            }

            await onNext?.(currentStep.id, nextStep.id)

            setIsTransitioning(false)

            setStepId(nextStep.id)
        }
    }

    const complete = async () => {
        setIsTransitioning(true)

        await onComplete?.()

        setIsTransitioning(false)
    }

    const close = async () => {
        if (!isCloseConfirmationActive && shouldConfirmClose(currentStep, recordForm)) {
            setIsCloseConfirmationActive(true)
        }
        else {
            setIsCloseConfirmationActive(false)

            setIsTransitioning(true)

            await onClose?.()

            setIsTransitioning(false)
        }
    }

    const cancelClose = () => {
        setIsCloseConfirmationActive(false)
    }

    return {
        recordForm,

        currentStep,
        currentStepIndex,
        setStep: setStepId,
        hasNext,
        isNextDisabled: !isValid || !hasNext,
        isSaving,
        isTransitioning,
        isCloseConfirmationActive,

        next,
        complete,
        close,
        cancelClose,
    }
}
